import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { createAutoLoginLink } from "../authService";
import { AppContext } from "../../context";

const SECONDS_IN_DAY = 24 * 60 * 60;
const DEFAULT_TTL_DAYS = 90;

const AutoLoginLinkGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AppContext) || {};
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [ttlDays, setTtlDays] = useState(String(DEFAULT_TTL_DAYS));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resultToken, setResultToken] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const hasStoreToken = Boolean(localStorage.getItem("storeAccessToken"));
    const hasAppToken = Boolean(localStorage.getItem("accessToken"));
    const isLoggedIn = Boolean(currentUser) || hasStoreToken || hasAppToken;
    if (!isLoggedIn) {
      navigate("/login", {
        replace: true,
        state: { from: `${location.pathname}${location.search || ""}` },
      });
    }
  }, [currentUser, navigate, location.pathname, location.search]);

  const canSubmit = useMemo(() => {
    const n = name.trim();
    const m = mobile.trim();
    if (!n || !m) return false;
    if (!/^[6-9]\d{9}$/.test(m)) return false;
    const ttl = Number(ttlDays);
    if (!Number.isFinite(ttl) || ttl <= 0) return false;
    return true;
  }, [name, mobile, ttlDays]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setCopied(false);
    setResultToken("");
    try {
      const body = {
        name: name.trim(),
        mobile: mobile.trim(),
        ttl_seconds: Math.round(Number(ttlDays) * SECONDS_IN_DAY),
      };
      const res = await createAutoLoginLink(body);
      const token = res?.token;
      if (typeof token !== "string" || !token.trim()) {
        throw new Error('Expected response shape: { token: "..." }');
      }
      setResultToken(token.trim());
    } catch (err) {
      setError(err?.error || err?.message || "Failed to generate link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!resultToken) return;
    try {
      await navigator.clipboard.writeText(resultToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // fallback: select text is handled by user
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 720 }}>
      <Card className="shadow-sm">
        <CardHeader className="bg-white">
          <div className="fw-bold">Store Magic Link Generator</div>
          <div className="text-muted small">
            Generates a secure auto-login link for sales sharing.
          </div>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Form onSubmit={handleGenerate}>
            <FormGroup>
              <Label className="fw-semibold">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Satheesh"
                className="unmask"
              />
            </FormGroup>

            <FormGroup>
              <Label className="fw-semibold">Mobile</Label>
              <Input
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="7358768746"
                inputMode="numeric"
                className="unmask"
              />
              <div className="text-muted small mt-1">
                Expected: 10-digit Indian mobile (no +91).
              </div>
            </FormGroup>

            <FormGroup>
              <Label className="fw-semibold">TTL (days)</Label>
              <Input
                value={ttlDays}
                onChange={(e) => setTtlDays(e.target.value)}
                className="unmask"
              />
              <div className="text-muted small mt-1">
                Default: {DEFAULT_TTL_DAYS} days (
                {DEFAULT_TTL_DAYS * SECONDS_IN_DAY} seconds)
              </div>
            </FormGroup>

            <Button
              color="primary"
              type="submit"
              disabled={!canSubmit || submitting}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" /> Generating…
                </>
              ) : (
                "Generate link"
              )}
            </Button>
          </Form>

          {resultToken && (
            <div className="mt-4">
              <Label className="fw-semibold">Generated token</Label>
              <Input readOnly value={resultToken} className="unmask" />
              <div className="d-flex align-items-center gap-2 mt-2">
                <Button color="secondary" outline onClick={handleCopy}>
                  Copy
                </Button>
                {copied && <div className="text-success small">Copied</div>}
              </div>
              <div className="text-muted small mt-2">
                Use it like: <code>/store/collections?t=TOKEN</code>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default AutoLoginLinkGenerator;

